import {
    _forEach, _groupBy, _map, _forOwn,
} from '@samclick-lodash';
import { formatDate, DEFAULT_FORMAT } from '@/utils/date-utils';
import { toMoney } from '@/utils/common-utils';

export class BarWithLineSetting {
    type: string = '' // line, bar

    legend: string = ''

    color: string | string[] = ''

    key: string = ''

    isMoney: boolean = false

    valuePosition: string = 'left'

    isDashed: boolean = false

    constructor(type: string = '', legend: string = '', color: string | string[] = '', key: string = '', valuePosition: string = 'left', isMoney: boolean = false, isDashed: boolean = false) {
        this.type = type; // 'date' for date
        this.legend = legend;
        this.color = color;
        this.key = key;
        this.isMoney = isMoney;
        this.valuePosition = valuePosition;
        this.isDashed = isDashed;
    }
}

export class BarWithLineConfig {
    constructor(groupBy: string = '', config: BarWithLineSetting[] = [], dateFormat: string = DEFAULT_FORMAT, hideLegend: boolean = false) {
        this.groupBy = groupBy;
        this.dateFormat = dateFormat;
        this.config = config;
        this.hideLegend = hideLegend;
    }

    groupBy: string = ''

    dateFormat: string = '' // optional if groupBy is date only

    config: BarWithLineSetting[] = []

    hideLegend: boolean = false
}

function extractValues(tempData: any, option: BarWithLineSetting) {
    let values: object[] = [];
    _forOwn(tempData, (value) => {
        values = [...values, ...value];
    });

    return _map(values, v => (option.isMoney ? toMoney(v[option.key], 2, false) : v[option.key]));
}

function extractKeys(tempData: any, config: BarWithLineConfig) {
    const keys = Object.keys(tempData);
    if (config.groupBy === 'date') {
        const dateKeys: string[] = [];
        _forEach(keys, (key) => {
            dateKeys.push(formatDate(key, config.dateFormat));
        });

        return dateKeys;
    }
    return keys;
}


function buildDataSets(config: BarWithLineConfig, data: object[]) {
    const tempData = _groupBy(data, config.groupBy);

    let datasets: object[] = [];
    _forEach(config.config, (option) => {
        const sett: any = {
            type: option.type,
            label: option.legend,
            borderWidth: 2,
            data: extractValues(tempData, option),
            yAxisID: option.valuePosition,
        };

        if (option.type === 'line') {
            sett.fill = false;
            sett.borderColor = option.color;
            if (option.isDashed) {
                sett.borderDash = [10, 5];
            }
        } else if (option.type === 'bar') {
            sett.borderColor = 'white';
            sett.backgroundColor = option.color;
        }
        datasets = [...datasets, sett];
    });

    return {
        labels: extractKeys(tempData, config),
        datasets,
    };
}

export function generateBarLine(config: BarWithLineConfig, data: object[] = [], parentProps: object) {
    return {
        chartData: buildDataSets(config, data),
        chartOptions: {
            responsive: true,
            maintainAspectRatio: false,
            title: {
                display: true,
                text: parentProps.title,
            },
            legend: {
                display: !config.hideLegend,
                labels: {
                    usePointStyle: true,
                },
            },
            tooltips: {
                mode: 'index',
                intersect: true,
                callbacks: {
                    label(tooltipItem, data) {
                        let tooltipValue = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                        const tootTiplabel = data.datasets[tooltipItem.datasetIndex].label;

                        if (tooltipValue.toString().indexOf('.') !== -1) {
                            tooltipValue = toMoney(tooltipValue);
                        } else {
                            tooltipValue = tooltipValue.toString();
                        }

                        return `${tootTiplabel} : ${tooltipValue}`;
                    },
                },
            },
            scales: {
                yAxes: [{
                    type: 'linear',
                    display: true,
                    position: 'left',
                    id: 'left',
                    ticks: {
                        beginAtZero: true,
                        userCallback(value, index, values) {
                            // Convert the number to a string and splite the string every 3 charaters from the end
                            value = value.toString();
                            value = value.split(/(?=(?:...)*$)/);
                            value = value.join(',');
                            return value;
                        },
                    },
                }, {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    id: 'right',
                    ticks: {
                        beginAtZero: true,
                        userCallback(value, index, values) {
                            // Convert the number to a string and splite the string every 3 charaters from the end
                            value = value.toString();
                            value = value.split(/(?=(?:...)*$)/);
                            value = value.join(',');
                            return value;
                        },
                    },
                    gridLines: {
                        drawOnChartArea: false,
                    },
                }],
            },
        },
    };
}
